import { useEffect, useMemo, useState } from 'react';
import type { FactoryAgentSummary, SkillManifest, SkillUsage } from '@reef/schema';
import { api } from '../api.ts';

/**
 * Every agent this deployment can run, on one screen.
 *
 * This used to be two tabs — a "roster" of the factory's agents and a
 * "registry" of skills — and the first question anyone new asked was what
 * the difference was. There is none worth a tab: an agent *is* a skill, the
 * committed `SKILL.md` a workflow step names when it runs, and the roster was
 * the same documents with the factory's job titles on them. So this is one
 * list of skills, and the ones the factory gave a job to say so on their card.
 *
 * Cards rather than the table they replaced. The table's columns — steps,
 * flows, tools, lint, evals, failure rate — were the operator's questions,
 * and they are still answered, in the detail pane. What a person arriving here
 * for the first time needs is the other question, "what does this one do", and
 * a card has room for that sentence where a table cell did not.
 *
 * There is no edit button, and that is the design rather than an omission. A
 * skill is a committed document; changing one is a commit to
 * `skills/<name>/SKILL.md`, which is reviewable in a diff, revertible, and
 * attributable. A textarea here would be a second way to change what every
 * agent does, and the only one with no history.
 */
export function AgentsView() {
  const [roster, setRoster] = useState<FactoryAgentSummary[]>([]);
  const [factoryPresent, setFactoryPresent] = useState<boolean | null>(null);
  const [skills, setSkills] = useState<SkillManifest[]>([]);
  const [usage, setUsage] = useState<Record<string, SkillUsage>>({});
  const [evals, setEvals] = useState<Record<string, EvalStanding>>({});
  /*
   * The instructions of the skills the roster does not carry.
   *
   * The roster answers with every factory agent's whole document, so those
   * are searchable the moment the screen loads; the registry answers with a
   * description only. A search that promises the instructions and skips the
   * shared skills finds "rollback" in the release agent and never in the
   * rollout procedure, which is the more surprising miss. So the rest are
   * read one by one once both lists are in — a handful of small requests —
   * and searched as they arrive.
   */
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .factoryAgents()
      .then((result) => {
        if (!live) return;
        setRoster(result.agents);
        setFactoryPresent(result.workflowsPresent.length > 0);
      })
      .catch((caught) => live && setError((caught as Error).message));
    api
      .skillRegistry(USAGE_WINDOW_HOURS)
      .then((result) => {
        if (!live) return;
        setSkills(result.skills);
        setUsage(Object.fromEntries(result.usage.map((entry) => [entry.skill, entry])));
      })
      .catch((caught) => live && setError((caught as Error).message));
    api
      .skillEvals()
      .then((result) => live && setEvals(Object.fromEntries(result.summary.map((entry) => [entry.skill, entry]))))
      .catch(() => {
        // A deployment that has never run the suite has no results, which is a
        // normal state and not worth an error banner over the whole page.
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    // Not before the roster has answered: until then every skill looks
    // shared, and the twelve documents the roster is about to hand over
    // would be fetched a second time.
    if (factoryPresent === null) return;
    const carried = new Set(roster.map((agent) => agent.skill));
    const missing = skills.filter((skill) => !carried.has(skill.name) && !(skill.name in texts));
    if (missing.length === 0) return;
    let live = true;
    for (const skill of missing) {
      api
        .skill(skill.name)
        .then((result) => live && setTexts((current) => ({ ...current, [skill.name]: result.instructions })))
        .catch(() => {
          // The card still has its description to be found by; the text is
          // fetched again when the detail opens, which is where a failure to
          // read it is reported.
        });
    }
    return () => {
      live = false;
    };
    // `texts` is read to skip what is already held, not to re-run on every
    // arrival — that would issue the same requests again mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, skills, factoryPresent]);

  const agents = useMemo(() => join(roster, skills, texts), [roster, skills, texts]);

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return agents;
    // The instructions are searched too, not just the name: they are the part
    // a person is actually approving, and "which agent handles rollbacks" is
    // a question about the prose.
    return agents.filter((agent) =>
      [agent.name, agent.key, agent.summary, agent.instructions]
        .join('\n')
        .toLowerCase()
        .includes(needle),
    );
  }, [agents, filter]);

  const factory = shown.filter((agent) => agent.roster);
  const shared = shown.filter((agent) => !agent.roster);
  const open = selected ? agents.find((agent) => agent.key === selected) ?? null : null;

  return (
    <div className={`page wide agents-page ${open ? 'with-detail' : ''}`}>
      <header className="page-head">
        <div>
          <h1>Agents</h1>
          <p className="page-lede">
            An agent is a set of written instructions that a workflow step runs. Every workflow
            picks its agents from this list; to change what one does, edit its instructions
            file and commit it.
          </p>
        </div>
        <div className="row">
          <input
            className="studio-filter"
            placeholder="Search by name or instructions"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            aria-label="Filter agents"
          />
        </div>
      </header>

      {error && <div className="banner danger">{error}</div>}

      <div className="agents-layout">
        <div className="agents-list">
          <section className="page-section">
            <div className="section-head">
              <h2>Factory agents</h2>
              <span className="hint">Each owns one job in the software factory.</span>
            </div>
            {factoryPresent === false && !error && (
              <p className="hint">
                No workflow here runs a factory agent yet. Build one under Workflows, or run{' '}
                <code>pnpm seed:local</code> to load the factory's own.
              </p>
            )}
            <AgentGrid
              agents={factory}
              usage={usage}
              evals={evals}
              selected={selected}
              onSelect={setSelected}
            />
          </section>

          <section className="page-section">
            <div className="section-head">
              <h2>Shared instructions</h2>
              <span className="hint">
                Mounted alongside an agent: standards to review against, a procedure to follow.
              </span>
            </div>
            <AgentGrid
              agents={shared}
              usage={usage}
              evals={evals}
              selected={selected}
              onSelect={setSelected}
            />
          </section>

          {shown.length === 0 && agents.length > 0 && (
            <p className="hint">No agent matches “{filter}”.</p>
          )}
        </div>

        {open && (
          <AgentDetail
            key={open.key}
            agent={open}
            usage={usage[open.key]}
            standing={evals[open.key]}
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- the list

function AgentGrid({
  agents,
  usage,
  evals,
  selected,
  onSelect,
}: {
  agents: readonly AgentEntry[];
  usage: Record<string, SkillUsage>;
  evals: Record<string, EvalStanding>;
  selected: string | null;
  onSelect(key: string): void;
}) {
  if (agents.length === 0) return null;
  return (
    <div className="agent-grid">
      {agents.map((agent) => {
        const health = healthOf(agent, evals[agent.key]);
        const used = usage[agent.key];
        return (
          <button
            key={agent.key}
            type="button"
            className="agent-card"
            aria-current={selected === agent.key ? 'true' : undefined}
            onClick={() => onSelect(agent.key)}
          >
            <span className="agent-card-head">
              <span className="agent-card-name">{agent.name}</span>
              {health && <span className={`pill ${health.tone}`}>{health.label}</span>}
            </span>
            <span className="agent-card-summary">{agent.summary}</span>
            <span className="agent-card-meta">
              <span>{whereItRuns(agent)}</span>
              {used && used.runs > 0 && (
                <span>
                  {used.runs} {used.runs === 1 ? 'run' : 'runs'} this week
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------- the detail

function AgentDetail({
  agent,
  usage,
  standing,
  onClose,
}: {
  agent: AgentEntry;
  usage: SkillUsage | undefined;
  standing: EvalStanding | undefined;
  onClose(): void;
}) {
  const [detail, setDetail] = useState<{
    instructions: string;
    proposals: Array<{ id: string; claim: string; state: string }>;
  } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .skill(agent.key)
      .then((result) => live && setDetail({ instructions: result.instructions, proposals: result.proposals }))
      .catch((caught) => live && setProblem((caught as Error).message));
    return () => {
      live = false;
    };
  }, [agent.key]);

  const findings = agent.skill?.findings ?? [];
  const roster = agent.roster;
  // The roster already carries the whole document, so a factory agent's
  // instructions are on screen before the fetch answers; the fetch is what
  // brings the proposals and, for a shared skill, the text itself.
  const instructions = detail?.instructions ?? agent.instructions;

  return (
    <aside className="agent-detail" aria-label={`${agent.name} detail`}>
      <div className="agent-detail-head">
        <div>
          <h2>{agent.name}</h2>
          <span className="hint">
            committed at <code>skills/{agent.key}/SKILL.md</code>
          </span>
        </div>
        <button className="ghost" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      <div className="agent-detail-body">
        <p className="agent-detail-summary">{agent.summary}</p>

        {roster && (
          <dl className="studio-facts">
            <dt>When it runs</dt>
            <dd>{roster.triggeredBy}</dd>
            <dt>What it produces</dt>
            <dd>{roster.produces}</dd>
            <dt>Model</dt>
            <dd>
              <code>{roster.model}</code>
            </dd>
            <dt>Tools</dt>
            <dd>
              {roster.tools.length === 0 ? (
                <span className="hint">None yet — no workflow step runs it.</span>
              ) : (
                roster.tools.map((tool) => (
                  <span key={tool} className="chip">
                    {tool}
                  </span>
                ))
              )}
            </dd>
          </dl>
        )}

        <dl className="studio-facts">
          <dt>Workflows</dt>
          <dd>
            {agent.workflows.length === 0 ? (
              <span className="hint">Not used by any workflow yet.</span>
            ) : (
              agent.workflows.map((id) => (
                <span key={id} className="chip">
                  {id}
                </span>
              ))
            )}
          </dd>
          <dt>Last 7 days</dt>
          <dd>
            {!usage || usage.runs === 0
              ? 'No runs'
              : `${usage.runs} ${usage.runs === 1 ? 'run' : 'runs'}${
                  // Null while nothing has ended — not zero. A new skill and a
                  // skill that has never failed are different facts.
                  usage.failureRate === null
                    ? ', none finished yet'
                    : `, ${Math.round(usage.failureRate * 100)}% failed`
                }`}
          </dd>
          <dt>Evals</dt>
          <dd>{describeEvals(standing)}</dd>
          <dt>Lint</dt>
          <dd>{findings.length === 0 ? 'Clean' : `${findings.length} ${findings.length === 1 ? 'finding' : 'findings'}`}</dd>
        </dl>

        {findings.map((finding) => (
          <div
            key={`${finding.rule}-${finding.line ?? 0}`}
            className={`banner ${finding.severity === 'error' ? 'danger' : 'warn'}`}
          >
            <strong>{finding.rule}</strong> — {finding.message}
          </div>
        ))}

        {problem && <div className="banner danger">{problem}</div>}

        {detail?.proposals.length ? (
          <>
            <p className="section-title">Proposals waiting</p>
            <ul className="agent-proposals">
              {detail.proposals.map((proposal) => (
                <li key={proposal.id}>
                  <span>{proposal.claim}</span>
                  <span className="chip">{proposal.state}</span>
                </li>
              ))}
            </ul>
            <p className="hint">
              Accepting one is a commit that bumps this skill’s version, which is why they wait
              here rather than being applied.
            </p>
          </>
        ) : null}

        <p className="section-title">Instructions</p>
        {/*
          The whole skill, not an excerpt. This is the document a human approves
          instead of approving every pull request the agent opens, and an
          approval given to a summary is not an approval.
        */}
        <pre className="studio-instructions">{instructions || 'Loading…'}</pre>
      </div>
    </aside>
  );
}

// ------------------------------------------------------------------ the join

/** One card's worth: a skill, and the factory job it holds if it holds one. */
interface AgentEntry {
  /** The skill name — what a workflow step writes to run it. */
  key: string;
  name: string;
  /** One line: the factory's responsibility, or the skill's own description. */
  summary: string;
  instructions: string;
  skill: SkillManifest | null;
  roster: FactoryAgentSummary | null;
  /** Workflow ids with a step that runs it, from either source. */
  workflows: string[];
}

interface EvalStanding {
  cases: number;
  passed: number;
  failed: number;
  skipped: number;
}

const USAGE_WINDOW_HOURS = 24 * 7;

/**
 * Roster first, in the factory's own order, then every other skill by name.
 *
 * Joined on the skill name rather than on an id: the roster entry names the
 * skill it runs, and that name is the one thing a workflow document, the
 * registry and the roster all agree on.
 */
function join(
  roster: readonly FactoryAgentSummary[],
  skills: readonly SkillManifest[],
  texts: Record<string, string>,
): AgentEntry[] {
  const bySkill = new Map(skills.map((skill) => [skill.name, skill]));
  const entries: AgentEntry[] = roster.map((agent) => {
    const skill = bySkill.get(agent.skill) ?? null;
    return {
      key: agent.skill,
      name: agent.name,
      summary: agent.responsibility,
      instructions: agent.instructions,
      skill,
      roster: agent,
      workflows: [...new Set([...agent.workflows, ...(skill?.usedBy ?? [])])],
    };
  });
  const taken = new Set(entries.map((entry) => entry.key));
  for (const skill of [...skills].sort((a, b) => a.name.localeCompare(b.name))) {
    if (taken.has(skill.name)) continue;
    entries.push({
      key: skill.name,
      name: skill.name,
      summary: skill.description || 'No description yet.',
      instructions: texts[skill.name] ?? '',
      skill,
      roster: null,
      workflows: skill.usedBy ?? [],
    });
  }
  return entries;
}

function whereItRuns(agent: AgentEntry): string {
  const count = agent.workflows.length;
  if (count === 0) return 'Not used by any workflow yet';
  return `Runs in ${count} ${count === 1 ? 'workflow' : 'workflows'}`;
}

/**
 * The one thing wrong with a skill, when something is.
 *
 * A card carries at most one status, and a quiet card means nothing needs
 * doing — twelve cards each wearing "clean · passing · 0 errors" is a screen
 * where nothing stands out. A failing eval outranks a lint error because it
 * is about behaviour rather than the shape of the file.
 */
function healthOf(agent: AgentEntry, standing: EvalStanding | undefined): { tone: string; label: string } | null {
  if (standing && standing.failed > 0) {
    return { tone: 'danger', label: `${standing.failed} eval${standing.failed === 1 ? '' : 's'} failing` };
  }
  const errors = (agent.skill?.findings ?? []).filter((finding) => finding.severity === 'error').length;
  if (errors > 0) return { tone: 'warn', label: `${errors} lint error${errors === 1 ? '' : 's'}` };
  if (agent.skill?.superseded) return { tone: '', label: 'superseded' };
  return null;
}

function describeEvals(standing: EvalStanding | undefined): string {
  // Said plainly rather than left blank: a skill with no case is one whose
  // behaviour nothing protects, and a blank cell reads as "fine".
  if (!standing || standing.cases === 0) return 'No cases yet';
  if (standing.failed > 0) return `${standing.failed} of ${standing.cases} failing`;
  // Skipped is not passed. A suite that reported green on a machine that
  // executed nothing is the failure this distinction exists to prevent.
  if (standing.passed === 0) return `${standing.cases} not run yet`;
  return `${standing.passed} of ${standing.cases} passing`;
}
